import { useHistory, useLocation, useParams } from "react-router-dom";
import OrderCreatedCheck from "../../assets/svg/OrderCreatedCheck";
import CustomText from "../../components/atomic/CustomText";
import DetailRenderer from "../../components/global/forms/DetailRenderer";
import Button from "../../components/organism/Button";
import FormTabCheck from "../../assets/svg/FormTabCheck";
import AdminContentLayout from "../../layouts/AdminContentLayout";
import useOrderDetail from "../../services/utils/custom hooks/OnlineOrder/useOrderDetail";
import useTestsList from "../../services/utils/custom hooks/OnlineOrder/useTestsList";
import { Fragment, useContext, useEffect, useState } from "react";
import useOrganizations from "../../services/utils/custom hooks/useOrganizations";
import usePhysiciansOfOrg from "../../services/utils/custom hooks/usePhysiciansOfOrg";
import OrderCreatedLoadingScreenIndicator from "../../components/global/LoadingIndicators/OnlineOrder/OrderCreatedScreenLoadingIndicator";
import useQueryParams from "../../services/utils/custom hooks/QueryHelper";
import { useQueryClient } from "@tanstack/react-query";
import PatientSelectionPopup from "../../components/popups/patientSelectionPopup";
import OrganizationAndClinicianSelection from "../../components/popups/organizationAndClinicianSelectionPopup";
import { PopUpContext } from "../../services/context/popupContext";
import { useOktaAuth } from "@okta/okta-react";
import FileActions from "../../services/utils/FileActions";
import toastMessages from "../../services/utils/helpers/toastMessage";

function OrderSteps(): JSX.Element {
  const steps = [
    "select test",
    "clinical information",
    "additional information",
    "laboratory information",
    "billing information",
    "supporting documents",
  ];
  return (
    <div className="bg-sideNavGray border-r py-4 px-2 pl-3 overflow-hidden">
      {steps.map((step) => (
        <div key={step} className="mb-3 flex items-center ">
          <FormTabCheck
            className="mr-1 !text-xl"
            fill={"#18B5B7"}
            isvalid={"white"}
            stroke={"#18B5B7"}
          />
          <CustomText
            text={step}
            fontWeight={"font-semibold"}
            fontSize="text-sm"
            className="hover:font-semibold text-primary capitalize"
          />
        </div>
      ))}
    </div>
  );
}

function OrderDetailCard({ data }: { data: any }): JSX.Element {
  const patientData = {
    "Patient First Name":
      data?.patientFirstName ?? data?.patientInfo?.firstName ?? "",
    "Patient Last Name":
      data.patientLastName ?? data?.patientInfo?.lastName ?? "",
    "Date of Birth": data?.patientDOB ?? data?.patientInfo?.birthDate ?? "",
    Gender: data?.patientInfo?.gender ?? "",
  };

  const testInformationData = {
    "Test Name": data.testName,
    Organization: data.organization,
    Clinician: data.clinician,
  };

  return (
    <div className="p-6 border border-roundedInputBorder rounded-lg w-full my-2">
      <div>
        <CustomText text="Test Information" fontWeight="font-medium" />
        <DetailRenderer
          data={testInformationData}
          groupFields={["Test Name", "Organization", "Clinician"]}
          hiddenFields={[]}
          containerClass="!whitespace-normal"
          className="my-3"
        />
      </div>
      <div className="my-2">
        <CustomText text="Patient Information" fontWeight="font-medium" />
        <DetailRenderer
          data={patientData}
          groupFields={Object.keys(patientData)}
          hiddenFields={[]}
          obsecureFields={[
            "Patient First Name",
            "Patient Last Name",
            "Date of Birth",
            "Gender",
          ]}
          className="my-3"
          containerClass="!whitespace-normal"
        />
      </div>
    </div>
  );
}

function OrderCreatedScreen(): JSX.Element {
  let id = useQueryParams().get("id");
  const queryClient = useQueryClient();
  const oktaAuth = useOktaAuth();
  const profileData = queryClient.getQueryData<any>(["profile"]);
  const roles = profileData?.user?.roles ?? [];
  const history = useHistory();
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);
  const { isLoading, data: orderData }: any = useOrderDetail(id ?? "");
  const { data: testList, isLoading: loadingTestList } = useTestsList();
  const { data: organizations, isLoading: fetchingOrganizations } =
    useOrganizations();
  const { data: physicians, isLoading: fetchingPhysicians } =
    usePhysiciansOfOrg();

  const clinician =
    roles.length === 1 && roles.includes("Physician")
      ? profileData.user
      : physicians?.data?.find?.(
          (physician) => physician.id == orderData?.data?.physicianId
        );
  const organization = organizations?.data?.find?.(
    (org) => org.id == orderData?.data.organizationId
  );
  const [foundTest, setFoundTest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingOrder, setDownloadingOrder] = useState<boolean>(false);

  const isPLUPhysician =
    roles.includes("Physician") && roles.includes("PathLab User");
  const isPhysicianOnly =
    (roles.length === 1 && roles.includes("Physician")) || isPLUPhysician;
  const fileActions = new FileActions();

  const status = orderData?.data?.status ?? 10;
  const text =
    status > 30
      ? "Your order has been successfully submitted."
      : "The clinician has been notified about your test order. Your order will be successfully submitted after the clinician's approval.";
  const title = status > 30 ? "Order Submitted" : "Order Sent For Approval";

  const testInformation = {
    testName: foundTest?.name ?? "",
    organization: organization?.name ?? "",
    clinician: `${clinician?.firstName} ${clinician?.lastName}`,
  };

  // Prevent browser back button and redirect to dashboard
  useEffect(() => {
    // Push a new state to create a history entry
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      // Push state again to prevent going back
      window.history.pushState(null, "", window.location.href);
      // Redirect to dashboard
      history.replace("/access/dashboard");
    };

    // Listen for popstate event (triggered by back/forward buttons)
    window.addEventListener("popstate", handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [history]);

  const openPaitentFlowPopup = () => {
    setPopUpTitle("Choose or Create a Patient");
    setPopUp(<PatientSelectionPopup oktaAuth={oktaAuth} />);
    setOnlineOrder?.(false);
  };

  function handleNewOnlineOrder() {
    setVisible(!visible);
    setPopUpTitle(
      `${
        isPhysicianOnly
          ? "Select an organisation"
          : "Select an organisation and a clinician"
      }`
    );
    setPopUp(
      <OrganizationAndClinicianSelection handleNext={openPaitentFlowPopup} />
    );
  }

  async function handleOrderDownload() {
    let testName = !!orderData?.data?.testTypes?.name
      ? orderData.data?.testTypes?.name.split(":")[0]
      : "";
    let fileName = `${orderData?.data?.id}_${
      !!orderData?.data?.patientName
        ? `${orderData.data?.patientName} `
        : "Patient"
    }${testName}`;
    setDownloadingOrder(true);
    try {
      const response = await fileActions.downloadMultiplePDF(
        [{ id: orderData?.data?.id, fileName }],
        oktaAuth.authState?.accessToken?.accessToken ?? ""
      );
      setDownloadingOrder(false);
    } catch (error: any) {
      setDownloadingOrder(false);
      toastMessages.error(
        error?.response?.data?.message ??
          error.message ??
          "Something went wrong"
      );
      setDownloadingOrder(false);
    }
    setDownloadingOrder(false);
  }

  useEffect(() => {
    setLoading(true);
  }, [id]);

  useEffect(() => {
    if (testList?.data) {
      testList.data.forEach((test) => {
        const found = test.testTypes.find(
          (testType) => testType.id == orderData?.data?.testTypeId
        );
        if (found) {
          setFoundTest(found);
        }
      });
    }
    setTimeout(
      () =>
        setLoading(
          isLoading ||
            loadingTestList ||
            fetchingPhysicians ||
            fetchingOrganizations
        ),
      1000
    );
  }, [testList, isLoading]);

  return (
    <AdminContentLayout
      pageTitle="Order Created | CSTL"
      subHeaderTitle={`Online Order / ${
        orderData?.data?.patientInfo?.firstName ?? ""
      } ${orderData?.data?.patientInfo?.lastName ?? ""}`}
    >
      <div className="grid lg:grid-cols-twoColumnSettingsPageLayout h-[85.9vh] overflow-auto">
        <div className="h-full bg-sideNavGray hidden lg:block">
          <OrderSteps />
        </div>
        {loading ? (
          <OrderCreatedLoadingScreenIndicator />
        ) : (
          <div className="flex flex-col pb-7 md:pb-4 justify-center items-center px-[5%] md:px-[10%] lg:px-[25%] w-full md:w-[100%] lg:w-full">
            <OrderCreatedCheck className="my-5" />
            <CustomText
              fontWeight="font-medium"
              fontSize="text-3xl"
              text={title}
              className="text-center"
            />
            <CustomText
              fontWeight="font-medium"
              fontSize="text-sm"
              text={text}
              className="my-3.5 text-center"
            />
            <OrderDetailCard
              data={{ ...orderData?.data, ...testInformation }}
            />
            <div className="w-full grid grid-cols-2 gap-4 md:flex md:flex-row md:justify-between my-4">
              <Button
                textInformation={{
                  text: "Go to dashboard",
                  fontWeight: "font-semibold",
                }}
                onClick={() => history.replace("/access/dashboard")}
                buttonTheme="secondary"
              />
              <Button
                buttonTheme="secondary"
                textInformation={{
                  text: `${
                    orderData?.data?.status < 40
                      ? "Download Order"
                      : "Download receipt"
                  }`,
                  fontWeight: "font-semibold",
                }}
                disabled={downloadingOrder || loading}
                loading={downloadingOrder}
                onClick={handleOrderDownload}
              />
              <Button
                textInformation={{
                  text: "Review Order",
                  fontWeight: "font-semibold",
                }}
                onClick={() =>
                  history.replace(
                    `/access/test-orders/summary/${orderData?.data?.id}`
                  )
                }
                buttonTheme="secondary"
              />
              <Button
                textInformation={{
                  text: "New online order",
                  fontWeight: "font-semibold",
                }}
                onClick={() => handleNewOnlineOrder()}
                buttonTheme="primary"
              />
            </div>
          </div>
        )}
      </div>
    </AdminContentLayout>
  );
}

export default OrderCreatedScreen;